import Cookies from "js-cookie";
import type {Session} from "@supabase/supabase-js";
import type {DealerUser, Dealership} from "@/types";

const AUTH_COOKIE_EXPIRY_DAYS = 7;
const ACCESS_TOKEN_COOKIE = "wheela_access_token";
const REFRESH_TOKEN_COOKIE = "wheela_refresh_token";

export type AuthAccountDetails = {
    accountType: Dealership["type"];
    firstName?: string;
    lastName?: string;
    name?: string;
    phone?: string;
};

export function buildDealershipName(details: AuthAccountDetails, fallback = "dealer") {
    if (details.accountType === "company") {
        return details.name?.trim() || fallback;
    }

    const fullName = [details.firstName?.trim(), details.lastName?.trim()].filter(Boolean).join(" ").trim();
    return fullName || fallback;
}

export function buildAuthMetadata(details: AuthAccountDetails) {
    return details.accountType === "individual"
        ? {
            accountType: details.accountType,
            firstName: details.firstName?.trim() ?? "",
            lastName: details.lastName?.trim() ?? ""
        }
        : {
            accountType: details.accountType,
            name: details.name?.trim() ?? ""
        };
}

export function buildDealershipSeed(details: AuthAccountDetails, _userId: string, email: string): Omit<Dealership, "id"> {
    const emailPrefix = email.split("@")[0] || "dealer";

    return {
        type: details.accountType,
        name: buildDealershipName(details, emailPrefix),
        images: [],
        profile: undefined,
        banner: undefined,
        email,
        phone: details.phone ?? "",
        phoneNumber: details.phone ?? "",
        locations: [],
        rating: 0,
        description: "",
        listingCount: 0,
        auctionCount: 0,
        soldCount: 0,
        views: 0
    };
}

export function buildDealerUserSeed(
    details: AuthAccountDetails,
    email: string
): Omit<DealerUser, "id" | "dealerId"> {
    return {
        firstName: details.firstName?.trim() ?? "",
        lastName: details.lastName?.trim() ?? "",
        email,
        role: "admin",
        profile: undefined
    };
}

export function getInitials(value?: string) {
    if (!value) return "";

    return value
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("");
}

export function normalizeError(error: unknown, fallback: string) {
    if (typeof error === "string") return error;
    if (error && typeof error === "object" && "message" in error) {
        return String((error as {message?: unknown}).message ?? fallback);
    }
    return fallback;
}

export function setAuthCookies(session: Pick<Session, "access_token" | "refresh_token">) {
    Cookies.set(ACCESS_TOKEN_COOKIE, session.access_token, {expires: AUTH_COOKIE_EXPIRY_DAYS, sameSite: "lax"});
    Cookies.set(REFRESH_TOKEN_COOKIE, session.refresh_token, {expires: AUTH_COOKIE_EXPIRY_DAYS, sameSite: "lax"});
}

export function getAuthCookies() {
    const accessToken = Cookies.get(ACCESS_TOKEN_COOKIE);
    const refreshToken = Cookies.get(REFRESH_TOKEN_COOKIE);

    if (!accessToken || !refreshToken) {
        return undefined;
    }

    return {accessToken, refreshToken};
}

export function clearAuthCookies() {
    Cookies.remove(ACCESS_TOKEN_COOKIE);
    Cookies.remove(REFRESH_TOKEN_COOKIE);
}
